import React, { useRef, useState } from 'react';

import { useTranslation } from '@/i18n';
import { colors } from '@/assets/styles/colors';
import { Card } from '@/components/ui/cards';
import { Selector } from '@/components/ui/inputs/selector';
import { PrimaryButton } from '@/components/ui/buttons';
import { Tooltip } from '@/components/ui/tooltips/Tooltip';
import { IconScout } from '@/components/ui/images/IconScout';
import { PermissionGuard } from '@/components/layout';
import { EmployeeDimensionalFilter } from '@/components/features/attendance/EmployeeDimensionalFilter';
import { useRecalculoFilters } from '@/hooks/attendance/recalculation/useRecalculoFilters';
import { useEmployeeDimensionalFilter } from '@/hooks/attendance/common/useEmployeeDimensionalFilter';
import type { DimensionalFilterConfig } from '@/hooks/attendance/common/useEmployeeDimensionalFilter';
import type { RecalculationProcessParams } from '@/api/domains/recalculation';


const DIMENSIONAL_CONFIG: DimensionalFilterConfig = {
  company: true,
  area: true,
  position: true,
  costCenter: true,
  securityGroup: true,
  contractModality: true,
  employeeStatus: true,
  employee: true,
};

interface RecalculoFiltersProps {
  isRecalculating: boolean;
  onRecalculate: (params: RecalculationProcessParams) => void;
  onStop: () => void;
}

export const RecalculoFilters: React.FC<RecalculoFiltersProps> = ({
  isRecalculating,
  onRecalculate,
  onStop,
}) => {
  const dimensionalFilterState = useEmployeeDimensionalFilter(DIMENSIONAL_CONFIG);
  const {
    selectedYear,
    selectedMonth,
    selectedDescriptionId,
    yearOptions,
    monthOptions,
    descriptionOptions,
    isLoadingYears,
    isLoadingMonths,
    isLoadingDescriptions,
    onYearChange,
    onMonthChange,
    onDescriptionChange,
    handleRecalculate,
  } = useRecalculoFilters({ onRecalculate, dimensionalFilterState });
  const { t: translate } = useTranslation();
  const yearWrapperRef = useRef<HTMLDivElement>(null);
  const monthWrapperRef = useRef<HTMLDivElement>(null);
  const descriptionWrapperRef = useRef<HTMLDivElement>(null);
  const [activeTooltip, setActiveTooltip] = useState<'year' | 'month' | 'description' | null>(null);

  return (
    <Card>
      <div className="flex flex-col gap-4 p-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div
            ref={yearWrapperRef}
            className="relative"
            onMouseEnter={() => isRecalculating && setActiveTooltip('year')}
            onMouseLeave={() => setActiveTooltip(null)}
          >
            <Selector
              data-testid="select-year"
              label={translate('attendance:recalculo.filters.year')}
              placeholder={translate('attendance:recalculo.filters.yearPlaceholder')}
              value={selectedYear ?? undefined}
              onChange={(value) => { if (typeof value === 'string') onYearChange(value); }}
              options={yearOptions}
              isLoading={isLoadingYears}
              disabled={isRecalculating}
            />
            {activeTooltip === 'year' && isRecalculating && (
              <Tooltip
                body={translate('attendance:recalculo.filters.yearDisabledTooltip')}
                anchorRef={yearWrapperRef}
                placement="top"
              />
            )}
          </div>

          <div
            ref={monthWrapperRef}
            className="relative"
            onMouseEnter={() => isRecalculating && setActiveTooltip('month')}
            onMouseLeave={() => setActiveTooltip(null)}
          >
            <Selector
              data-testid="select-month"
              label={translate('attendance:recalculo.filters.month')}
              placeholder={translate('attendance:recalculo.filters.monthPlaceholder')}
              value={selectedMonth ?? undefined}
              onChange={(value) => { if (typeof value === 'string') onMonthChange(value); }}
              options={monthOptions}
              isLoading={isLoadingMonths}
              disabled={!selectedYear || isRecalculating}
            />
            {activeTooltip === 'month' && isRecalculating && (
              <Tooltip
                body={translate('attendance:recalculo.filters.monthDisabledTooltip')}
                anchorRef={monthWrapperRef}
                placement="top"
              />
            )}
          </div>

          <div
            ref={descriptionWrapperRef}
            className="relative"
            onMouseEnter={() => isRecalculating && setActiveTooltip('description')}
            onMouseLeave={() => setActiveTooltip(null)}
          >
            <Selector
              data-testid="select-description"
              label={translate('attendance:recalculo.filters.description')}
              placeholder={translate('attendance:recalculo.filters.descriptionPlaceholder')}
              value={selectedDescriptionId ?? undefined}
              onChange={(value) => { if (typeof value === 'string') onDescriptionChange(value); }}
              options={descriptionOptions}
              isLoading={isLoadingDescriptions}
              disabled={!selectedMonth || isRecalculating}
            />
            {activeTooltip === 'description' && isRecalculating && (
              <Tooltip
                body={translate('attendance:recalculo.filters.descriptionDisabledTooltip')}
                anchorRef={descriptionWrapperRef}
                placement="top"
              />
            )}
          </div>

          <div className="flex items-end">
            <PermissionGuard action="edit">
              {isRecalculating ? (
                <PrimaryButton
                  data-testid="btn-stop"
                  variant="outline"
                  onClick={onStop}
                >
                  {translate('attendance:recalculo.actions.stop')}
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  data-testid="btn-recalculate"
                  onClick={handleRecalculate}
                  disabled={!selectedDescriptionId}
                >
                  {translate('attendance:recalculo.actions.recalculate')}
                </PrimaryButton>
              )}
            </PermissionGuard>
          </div>
        </div>

        <button
          type="button"
          onClick={dimensionalFilterState.toggleExpanded}
          disabled={isRecalculating}
          className="self-start flex items-center gap-1 font-medium text-sm cursor-pointer bg-transparent border-none p-0"
          style={{ color: colors.important.main }}
          aria-expanded={dimensionalFilterState.isExpanded}
          aria-controls="advanced-filters-panel"
        >
          {dimensionalFilterState.isExpanded
            ? translate('attendance:dimensionalFilter.hide')
            : translate('attendance:dimensionalFilter.showAdvancedFilters')}
          <IconScout
            name={dimensionalFilterState.isExpanded ? 'angleUp' : 'angleDown'}
            size={16}
          />
        </button>

        {dimensionalFilterState.isExpanded && (
          <div id="advanced-filters-panel">
            <EmployeeDimensionalFilter
              config={DIMENSIONAL_CONFIG}
              state={dimensionalFilterState}
              disabled={isRecalculating}
            />
          </div>
        )}

      </div>
    </Card>
  );
};

export default RecalculoFilters;
